import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-bootstrap';
import { Button, IconButton } from '@sumologic/yama-ds-components';

import { modalLiteralSizes, literalSizeDefault } from '~/modules/globalValues';

import PageOverlay from '~/components/PageOverlay/PageOverlay';
import Translate from '~/components/Translate/Translate';
import Icon from '~/components/Icon/Icon';

// prettier-ignore
const SimpleModal = props => {
    const {
        ['data-testid']: testId,
        id = null,
        title,
        titleIcon,
        titleSuffix,
        titleAtApply,
        onApply,
        applyLabel,
        disableApply,
        alreadyApplying = false,
        allowHideEvenApplyInProgress,
        onDismiss,
        dismissLabel,
        middleButtons,
        setShow,
        show,
        noHeader,
        noFooter,
        noApplyButton,
        children,
        className,
        size,
        useOverlay
    } = props;

    const [applyInProgress, setApplyInProgress] = useState(alreadyApplying);

    const disabledApply = disableApply || alreadyApplying || applyInProgress;

    const disabledHide = applyInProgress && !allowHideEvenApplyInProgress;

    const hideMe = () => {
        setShow(false);
    };

    const dismissMe = onDismiss
        ? (caseN, e = null) => {
              hideMe();
              onDismiss(caseN, e, {
                  applyInProgress,
                  allowHideEvenApplyInProgress
              });
          }
        : hideMe;

    const middleButtonSuccessCallback = middleButtons?.length
        ? e => {
              dismissMe(4, e);
          }
        : null;

    return (
        <>
            <Modal
                id={id}
                data-testid={testId}
                className={`simple-modal ${className || 'default'} ${
                    applyInProgress ? 'is' : 'not'
                }-applying`}
                size={
                    size && modalLiteralSizes.includes(size)
                        ? size
                        : literalSizeDefault
                }
                show={show ?? false}
                onHide={() => {
                    if (allowHideEvenApplyInProgress || !applyInProgress) {
                        dismissMe(0);
                    }
                }}
                scrollable
                centered
            >
                {noHeader ?? false ? null : (
                    <Modal.Header>
                        {title?.length ? (
                            <div className="modal-title">
                                {titleIcon
                                    ? <Icon type={titleIcon} />
                                    : null
                                }
                                <span className="text">
                                    <Translate
                                        message={
                                            titleAtApply?.length &&
                                            applyInProgress
                                                ? titleAtApply
                                                : title
                                        }
                                    />
                                </span>
                                {titleSuffix?.length ? (
                                    <>
                                        &ensp;
                                        <span className="modal-description">
                                            <Translate message={titleSuffix} />
                                        </span>
                                    </>
                                ) : null}
                            </div>
                        ) : null}
                        <IconButton
                            className="btn-text"
                            disabled={disabledHide}
                            onClick={() => {
                                dismissMe(1);
                            }}
                            size="small"
                        >
                          <Icon type="cross2" size="sm"/>
                        </IconButton>
                    </Modal.Header>
                )}
                <Modal.Body>{children}</Modal.Body>
                {noFooter ?? false ? null : (
                    <Modal.Footer>
                        {false !== dismissLabel ? (
                            <Button
                                variant="contained"
                                color="secondary"
                                className="me-3"
                                disabled={disabledHide}
                                onClick={event => {
                                    event.preventDefault();
                                    dismissMe(2);
                                }}
                            >
                                <Translate message={dismissLabel || 'Cancel'} />
                            </Button>
                        ) : null}
                        {middleButtonSuccessCallback && (
                            <span className="middle-buttons">
                                {middleButtons.map((e, i) => {
                                    const { label, fn } = e;
                                    return label?.length &&
                                        'function' === typeof fn ? (
                                        <Button
                                            key={i}
                                            variant="contained"
                                            color="secondary"
                                            className="me-3"
                                            disabled={applyInProgress}
                                            onClick={event => {
                                                event.preventDefault();
                                                setApplyInProgress(true);
                                                fn(middleButtonSuccessCallback);
                                            }}
                                        >
                                            <Translate message={label} />
                                        </Button>
                                    ) : null;
                                })}
                            </span>
                        )}
                        {noApplyButton || (disabledApply && allowHideEvenApplyInProgress) ? null : (
                            <Button
                                variant="contained"
                                color="primary"
                                className="me-3"
                                disabled={disabledApply}
                                onClick={
                                    disabledApply
                                        ? null
                                        : event => {
                                              setApplyInProgress(true);
                                              onApply(data => {
                                                  dismissMe(3, data);
                                              }, event);
                                          }
                                }
                            >
                                <Translate message={applyLabel || 'Save'} />
                            </Button>
                        )}
                    </Modal.Footer>
                )}
            </Modal>
            {useOverlay && applyInProgress && (
                <PageOverlay show={applyInProgress} />
            )}
        </>
    );
};

SimpleModal.propTypes = {
    title: PropTypes.string.isRequired,
    titleIcon: PropTypes.string,
    titleAtApply: PropTypes.string,
    applyLabel: PropTypes.string,
    onApply: PropTypes.func,
    alreadyApplying: PropTypes.bool,
    allowHideEvenApplyInProgress: PropTypes.bool,
    dismissLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    onDismiss: PropTypes.func,
    setShow: PropTypes.func.isRequired,
    middleButtons: PropTypes.array,
    size: PropTypes.string,
    show: PropTypes.bool,
    noHeader: PropTypes.bool,
    noFooter: PropTypes.bool,
    useOverlay: PropTypes.bool,
    className: PropTypes.string
};

/*
Note:
#
  Set "dismissLabel" exactly to 'false' omit the dismiss button in footer.

#
  the absence of the listed attributes is treated as 'false':
  - noHeader
  - noFooter
  - useOverlay

#
  When exist this string will be translated:
  - title
  - titleAtApply

*/

export default SimpleModal;
